import { CdkTrapFocus } from '@angular/cdk/a11y';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  DOCUMENT,
  effect,
  ElementRef,
  inject,
  input,
  model,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { Router } from '@angular/router';
import { TranslationService } from '../../translation-service';
import { AutoFocusDirective } from '../auto-focus-directive';
import { ScrollObserver } from '../scroll-observer-directive';
import { TranslationDirective } from '../translation-directive';

export type SidePanelPosition = 'left' | 'right';

const DEFAULT_WIDTH = '550px';

@Component({
  selector: 'upu-side-panel',
  imports: [
    NgTemplateOutlet,
    MatIcon,
    TranslationDirective,
    AutoFocusDirective,
    ScrollObserver,
  ],
  templateUrl: './side-panel.html',
  hostDirectives: [CdkTrapFocus],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidePanel {
  private readonly router = inject(Router, { optional: true });
  private readonly document = inject(DOCUMENT);
  private readonly translations = inject(TranslationService);

  readonly open = model(false);
  readonly title = input.required<string>();
  readonly width = input<string | number>(DEFAULT_WIDTH);
  readonly dismissible = input<boolean>(true);
  readonly closeOnNavigation = input<boolean>(true);

  headerTemplate = contentChild<TemplateRef<HTMLElement>>('header');
  footerTemplate = contentChild<TemplateRef<HTMLElement>>('footer');

  protected readonly isRtl = signal(this.translations.isRtl());
  protected readonly hasScrolled = signal(false);
  protected readonly markForClose = signal(false);
  protected readonly position = computed<SidePanelPosition>(() =>
    this.isRtl() ? 'left' : 'right',
  );

  private readonly headerEle =
    viewChild<ElementRef<HTMLHeadElement>>('headerEle');

  protected readonly headerHeight = signal(0);

  constructor() {
    effect(() => {
      this.document.documentElement.classList.toggle(
        'overflow-hidden',
        this.open(),
      );
    });

    effect(() => {
      if (
        !this.router ||
        !this.closeOnNavigation() ||
        !this.open() ||
        this.markForClose() ||
        !this.router.currentNavigation()
      ) {
        return;
      }

      this.close();
    });

    effect((onCleanup) => {
      const headerEle = this.headerEle()?.nativeElement;

      if (this.open() && headerEle) {
        const observer = new ResizeObserver((entries) => {
          this.headerHeight.set(entries[0].target.clientHeight);
        });

        observer.observe(headerEle);

        onCleanup(() => observer.disconnect());
      }
    });
  }

  protected close(): void {
    this.markForClose.set(true);
  }

  protected closeAnimationEnd(event: AnimationEvent): void {
    if (event.animationName === 'fade-out') {
      this.markForClose.set(false);
      this.open.set(false);
    }
  }
}
